import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Settings, Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw } from "lucide-react";

export default function AdminConfigPage() {
  const [stages, setStages] = useState([]);
  const [subPhases, setSubPhases] = useState({}); // { [stageName]: string[] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState({ stages: [], subPhases: {} });
  const [newStage, setNewStage] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workflow/stages");
      const st = r.data?.stages || [];
      const sp = r.data?.sub_phases || {};
      setStages(st);
      setSubPhases(sp);
      setOriginal({ stages: st, subPhases: sp });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    if (JSON.stringify(stages) !== JSON.stringify(original.stages)) return true;
    return JSON.stringify(subPhases) !== JSON.stringify(original.subPhases);
  }, [stages, subPhases, original]);

  const renameStage = (idx, val) => {
    setStages((prev) => {
      const oldName = prev[idx];
      const next = [...prev];
      next[idx] = val;
      // Move sub_phases key on rename
      if (oldName !== val && subPhases[oldName]) {
        setSubPhases((sp) => {
          const cp = { ...sp };
          cp[val] = cp[oldName];
          delete cp[oldName];
          return cp;
        });
      }
      return next;
    });
  };

  const removeStage = (idx) => {
    const name = stages[idx];
    if (!window.confirm(`Remove stage "${name}"? Existing ideas in this stage will keep the label but no longer match validation.`)) return;
    setStages((prev) => prev.filter((_, i) => i !== idx));
    setSubPhases((sp) => { const cp = { ...sp }; delete cp[name]; return cp; });
  };

  const moveStage = (idx, dir) => {
    setStages((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const addStage = () => {
    const val = newStage.trim();
    if (!val) return;
    if (stages.some((s) => s.toLowerCase() === val.toLowerCase())) {
      toast.error("Stage already exists");
      return;
    }
    setStages((prev) => [...prev, val]);
    setSubPhases((sp) => ({ ...sp, [val]: [] }));
    setNewStage("");
  };

  const addSubPhase = (stage, phase) => {
    const p = (phase || "").trim();
    if (!p) return;
    setSubPhases((sp) => ({ ...sp, [stage]: [...(sp[stage] || []), p] }));
  };

  const removeSubPhase = (stage, i) => {
    setSubPhases((sp) => ({ ...sp, [stage]: (sp[stage] || []).filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    if (stages.length === 0) { toast.error("At least one stage is required"); return; }
    setSaving(true);
    try {
      const r = await api.put("/admin/workflow/stages", { stages, sub_phases: subPhases });
      setOriginal({ stages: r.data.stages, subPhases: r.data.sub_phases });
      toast.success("Publication workflow saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save workflow");
    } finally { setSaving(false); }
  };

  const revert = () => {
    setStages(original.stages);
    setSubPhases(original.subPhases);
  };

  return (
    <div data-testid="admin-config-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PLATFORM CONFIGURATION</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Workflow &amp; <em className="italic text-kb-primary">platform config</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Customise the research lifecycle stages and their sub-phases. Changes take effect immediately for stage validation and milestone scaffolding on newly-created ideas.
      </p>

      <div className="mt-8 kb-card p-6" data-testid="publication-workflow-card">
        <div className="flex items-center justify-between mb-4">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PUBLICATION WORKFLOW</div>
          <div className="flex items-center gap-2">
            {dirty && (
              <button onClick={revert} className="kb-btn-secondary text-xs inline-flex items-center gap-1.5" data-testid="workflow-revert-btn">
                <RotateCcw className="w-3.5 h-3.5" /> Revert
              </button>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className="kb-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="workflow-save-btn"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : (
          <>
            <ol className="space-y-4" data-testid="workflow-stage-list">
              {stages.map((s, i) => (
                <StageRow
                  key={`${s}-${i}`}
                  index={i}
                  total={stages.length}
                  stage={s}
                  phases={subPhases[s] || []}
                  onRename={(val) => renameStage(i, val)}
                  onRemove={() => removeStage(i)}
                  onMove={(dir) => moveStage(i, dir)}
                  onAddPhase={(p) => addSubPhase(s, p)}
                  onRemovePhase={(pi) => removeSubPhase(s, pi)}
                />
              ))}
            </ol>

            <div className="mt-5 pt-4 border-t border-slate-200 flex items-center gap-2">
              <input
                value={newStage}
                onChange={(e) => setNewStage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addStage()}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                placeholder="Add a new stage (e.g. Ethics Approval)"
                data-testid="workflow-new-stage-input"
              />
              <button onClick={addStage} className="kb-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="workflow-add-stage-btn">
                <Plus className="w-4 h-4" /> Add stage
              </button>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 kb-card p-6">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-2">
          <Settings className="w-3.5 h-3.5" /> FEATURE FLAGS
        </div>
        <ul className="mt-4 space-y-2 text-sm text-kb-ink-muted">
          <li>· Plagiarism engine: <span className="text-amber-700 font-mono">MOCKED</span> — production replaces with Turnitin / iThenticate</li>
          <li>· AI-assisted mentor matching: <span className="text-slate-600 font-mono">DEFERRED</span> — rule-based scoring in v1</li>
          <li>· Object storage for attachments: <span className="text-slate-600 font-mono">METADATA-ONLY</span> — full upload pipeline coming</li>
          <li>· Multilingual surveys: <span className="text-emerald-700 font-mono">ENABLED</span> (en / ne)</li>
        </ul>
      </div>
    </div>
  );
}

function StageRow({ index, total, stage, phases, onRename, onRemove, onMove, onAddPhase, onRemovePhase }) {
  const [phaseInput, setPhaseInput] = useState("");
  return (
    <li className="border-l-2 border-kb-primary/30 pl-4" data-testid={`workflow-stage-${index}`}>
      <div className="flex items-center gap-3">
        <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary shrink-0">STAGE {String(index + 1).padStart(2, "0")}</span>
        <input
          value={stage}
          onChange={(e) => onRename(e.target.value)}
          className="flex-1 font-serif text-lg text-kb-ink bg-transparent border-b border-transparent hover:border-slate-300 focus:border-kb-primary outline-none px-1"
          data-testid={`workflow-stage-name-${index}`}
        />
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1 text-kb-ink-muted hover:text-kb-primary disabled:opacity-30" title="Move up">
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1 text-kb-ink-muted hover:text-kb-primary disabled:opacity-30" title="Move down">
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button onClick={onRemove} className="p-1 text-kb-ink-muted hover:text-rose-600" title="Remove stage" data-testid={`workflow-remove-stage-${index}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {phases.map((sp, pi) => (
          <span key={`${sp}-${pi}`} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink bg-slate-100 px-2 py-1 rounded-sm inline-flex items-center gap-1.5">
            {sp}
            <button onClick={() => onRemovePhase(pi)} className="text-kb-ink-muted hover:text-rose-600" title="Remove sub-phase" data-testid={`workflow-remove-phase-${index}-${pi}`}>
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <span className="inline-flex items-center gap-1">
          <input
            value={phaseInput}
            onChange={(e) => setPhaseInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { onAddPhase(phaseInput); setPhaseInput(""); }
            }}
            className="px-2 py-1 border border-slate-200 rounded-sm text-[10px] uppercase tracking-wider w-40"
            placeholder="+ sub-phase"
            data-testid={`workflow-new-phase-${index}`}
          />
          <button
            onClick={() => { onAddPhase(phaseInput); setPhaseInput(""); }}
            className="p-1 text-kb-primary hover:text-kb-primary-dark"
            title="Add sub-phase"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </span>
      </div>
    </li>
  );
}
